import { Button } from '@creditornot/cb-button';
import { Plus } from '@creditornot/cb-icons';
import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import { RouteWhenAuthenticated } from 'common/components';
import { CurrencyContext } from 'common/formatting/CurrencyContext';
import {
  ExperienceEntityType,
  PageHeader,
  useExperienceModuleContext,
  useSelectedEntityVenues,
} from 'modules/experience-core';
import MessagePage from 'modules/experience-core/components/MessagePage';
import { ExperienceMenu } from 'modules/menu/components/ExperienceMenu';
import { VatCategoriesParamsContextProvider } from 'modules/menu/components/VatCategories';

import { ActionsContainer } from './components/CommonStyles';
import { ScopeContextProvider } from './contexts/ScopeContext';
import { useIsSettingsPageEnabled } from './hooks/useIsSettingsPageEnabled';
import { useIsBrandSharedMenuEnabled, useIsMerchantSharedMenuEnabled } from './hooks/useIsSharedMenuEnabled';
import { SharedMenuRootPageV2 } from './pages/SharedMenuRootPageV2';
import { SharedMenuSettingsPage } from './pages/SharedMenuSettingsPage';
import { OfferingPlatformScope } from './types';

export const ExperienceListingManager = () => {
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();
  const { country, selectedExperienceEntity, currency } = useExperienceModuleContext();
  const { experienceEntityId, experienceEntityType, experienceEntityObject } = selectedExperienceEntity;
  const experienceVenueEntities = useSelectedEntityVenues();
  const venues = useMemo(
    () => experienceVenueEntities.map(experienceVenueEntity => experienceVenueEntity.experienceEntityObject),
    [experienceVenueEntities],
  );
  const isSettingsPageEnabled = useIsSettingsPageEnabled();
  const vatCategoriesParams = useMemo(() => ({ country }), [country]);
  const [isCreateSelectionModalOpen, setIsCreateSelectionModalOpen] = useState(false);
  const baseUrl = url.split('/shared-menu')[0].concat('/shared-menu');
  const isOnSubPage = pathname !== baseUrl;
  const history = useHistory();
  const { formatMessage } = useIntl();
  const enabledForBrand = useIsBrandSharedMenuEnabled(
    experienceEntityId,
    experienceEntityType === ExperienceEntityType.BRAND,
  );
  const enabledForMerchant = useIsMerchantSharedMenuEnabled(
    experienceEntityId,
    experienceEntityType === ExperienceEntityType.MERCHANT,
  );

  const listingMangerEnabled =
    experienceEntityType === ExperienceEntityType.MERCHANT ? enabledForMerchant : enabledForBrand;
  if (experienceEntityType === ExperienceEntityType.VENUE) {
    return (
      <CurrencyContext.Provider value={{ currency }}>
        <ExperienceMenu experienceVenueEntity={selectedExperienceEntity} />
      </CurrencyContext.Provider>
    );
  }

  if (experienceEntityType === ExperienceEntityType.ENTERPRISE || !listingMangerEnabled) {
    return (
      <MessagePage
        variant="not_supported"
        title={formatMessage({ id: 'shared-menu.select-business.title' })}
        description={formatMessage({ id: 'shared-menu.select-business.description' })}
      />
    );
  }

  const scope: OfferingPlatformScope = { type: experienceEntityType, id: experienceEntityId };

  return (
    <CurrencyContext.Provider value={{ currency }}>
      <VatCategoriesParamsContextProvider value={vatCategoriesParams}>
        <ScopeContextProvider value={scope}>
          <Switch>
            {isSettingsPageEnabled && (
              <RouteWhenAuthenticated exact path={`${path}/settings`}>
                <PageHeader
                  title={<FormattedMessage id="listing-manager.settings.title" />}
                  backLink={url}
                  backLinkText={<FormattedMessage id="shared-menu.settings-page.header.back-button-label" />}
                />
                <SharedMenuSettingsPage rootUrl={url} isExperienceFlow />
              </RouteWhenAuthenticated>
            )}

            <RouteWhenAuthenticated path={path}>
              <PageHeader enableSelector={!isOnSubPage}>
                <ActionsContainer>
                  {isSettingsPageEnabled && (
                    <div>
                      <Button variant="secondary" size="small" onClick={() => history.push(`${baseUrl}/settings`)}>
                        <FormattedMessage id="listing-manager.settings.title" />
                      </Button>
                    </div>
                  )}
                  <div>
                    <Button
                      icon={<Plus />}
                      variant="primary"
                      size="small"
                      onClick={() => setIsCreateSelectionModalOpen(true)}
                    >
                      <FormattedMessage id="listing-manager.create-menu.button-label" />
                    </Button>
                  </div>
                </ActionsContainer>
              </PageHeader>
              <SharedMenuRootPageV2
                country={country}
                venues={venues}
                merchant={experienceEntityType === ExperienceEntityType.MERCHANT ? experienceEntityObject : undefined}
                brand={experienceEntityType === ExperienceEntityType.BRAND ? experienceEntityObject : undefined}
                isCreateSelectionModalOpen={isCreateSelectionModalOpen}
                setIsCreateSelectionModalOpen={setIsCreateSelectionModalOpen}
                isExperienceFlow
                isSettingsPageEnabled={isSettingsPageEnabled}
              />
            </RouteWhenAuthenticated>
          </Switch>
        </ScopeContextProvider>
      </VatCategoriesParamsContextProvider>
    </CurrencyContext.Provider>
  );
};
